import {
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  ScrollArea,
  useDebounceCallback,
} from "@devhop/ui";
import { Store } from "lucide-react";
import { useState } from "react";
import type { BranchProductWithDetails } from "../../domain/types";
import {
  useBranchProducts,
  useCategories,
  usePrefetchProductConfig,
  useProductSearch,
} from "../api/queries";
import { usePOSContext } from "../context/POSContext";
import { CategoryTabs } from "./CategoryTabs";
import { ProductGrid } from "./ProductGrid";
import { ProductSearchBar } from "./ProductSearchBar";

type ProductMenuProps = {
  onProductClick: (product: BranchProductWithDetails) => void;
};

export function ProductMenu({ onProductClick }: ProductMenuProps) {
  const { selection } = usePOSContext();
  const branchId = selection?.branchId ?? null;

  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null,
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const isSearchMode = searchQuery.trim().length > 0;
  const prefetchProductConfig = usePrefetchProductConfig(branchId);

  const { data: categories, isLoading: categoriesLoading } =
    useCategories(branchId);
  const { data: productsData, isLoading: productsLoading } = useBranchProducts(
    branchId,
    searchQuery ? null : selectedCategoryId,
    pageSize,
    (page - 1) * pageSize,
    { enabled: !isSearchMode },
  );
  const { data: searchResults, isLoading: searchLoading } = useProductSearch(
    branchId,
    searchQuery,
  );
  const displayProducts = isSearchMode
    ? (searchResults ?? [])
    : (productsData?.data ?? []);
  const totalProducts = isSearchMode
    ? (searchResults?.length ?? 0)
    : (productsData?.total ?? 0);
  const isLoading = isSearchMode ? searchLoading : productsLoading;

  const handleBarcodeScan = (barcode: string) => {
    setSearchQuery(barcode);
    setSelectedCategoryId(null);
    setPage(1);
  };

  const handleCategorySelect = (categoryId: string | null) => {
    setSelectedCategoryId(categoryId);
    setSearchQuery("");
    setPage(1); // Reset to first page when category changes
  };

  const handleSearchChange = useDebounceCallback((value) => {
    setSearchQuery(value);
    setPage(1); // Reset to first page when search changes
    if (value.trim().length === 0) {
    }
  }, 500);

  // const handleSearchChange = (value: string) => {
  //   setSearchQuery(value);
  //   setPage(1); // Reset to first page when search changes
  //   if (value.trim().length === 0) {

  //   }
  // };

  if (!branchId) {
    return (
      <div className="flex h-full items-center justify-center">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Store className="h-8 w-8 text-muted-foreground" />
            </EmptyMedia>
            <EmptyTitle>ກະລຸນາເລືອກສາຂາກ່ອນເພື່ອເບິ່ງສິນຄ້າ</EmptyTitle>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="space-y-3 border-b bg-card/40 p-4">
        <ProductSearchBar
          defaultValue={searchQuery}
          onChange={handleSearchChange}
          onBarcodeScan={handleBarcodeScan}
        />
        {!isSearchMode && (
          <CategoryTabs
            categories={categories ?? []}
            selectedCategoryId={selectedCategoryId}
            onSelectCategory={handleCategorySelect}
            isLoading={categoriesLoading}
          />
        )}
      </div>
      <div className="flex min-h-0 flex-1 flex-col">
        <ScrollArea className="min-h-0 flex-1">
          <div className="p-4">
            <ProductGrid
              products={displayProducts}
              isLoading={isLoading}
              onProductClick={onProductClick}
              onProductHover={(product) => prefetchProductConfig(product.id)}
              emptyMessage={isSearchMode ? "ບໍ່ພົບສິນຄ້າທີ່ຄົ້ນຫາ" : "ບໍ່ພົບສິນຄ້າໃນຫມວດຫມູ່ນີ້"}
            />
          </div>
        </ScrollArea>
        {!isSearchMode && totalProducts > pageSize && (
          <div className="border-t bg-card/40 p-3">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setPage((p) => Math.max(1, p - 1));
                    }}
                    className={
                      page === 1
                        ? "pointer-events-none opacity-50"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>
                {(() => {
                  const totalPages = Math.ceil(totalProducts / pageSize);
                  const pages: (number | "ellipsis")[] = [];

                  if (totalPages <= 7) {
                    // Show all pages if 7 or fewer
                    for (let i = 1; i <= totalPages; i++) {
                      pages.push(i);
                    }
                  } else {
                    // Always show first page
                    pages.push(1);

                    if (page > 3) {
                      pages.push("ellipsis");
                    }

                    // Show pages around current page
                    const start = Math.max(2, page - 1);
                    const end = Math.min(totalPages - 1, page + 1);

                    for (let i = start; i <= end; i++) {
                      if (i !== 1 && i !== totalPages) {
                        pages.push(i);
                      }
                    }

                    if (page < totalPages - 2) {
                      pages.push("ellipsis");
                    }

                    // Always show last page
                    if (totalPages > 1) {
                      pages.push(totalPages);
                    }
                  }

                  let ellipsisIndex = 0;
                  return pages.map((p) => {
                    if (p === "ellipsis") {
                      ellipsisIndex++;
                      return (
                        <PaginationItem key={`ellipsis-${ellipsisIndex}`}>
                          <PaginationEllipsis />
                        </PaginationItem>
                      );
                    }
                    return (
                      <PaginationItem key={p}>
                        <PaginationLink
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            setPage(p);
                          }}
                          isActive={p === page}
                          className="cursor-pointer"
                        >
                          {p}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  });
                })()}
                <PaginationItem>
                  <PaginationNext
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setPage((p) =>
                        Math.min(Math.ceil(totalProducts / pageSize), p + 1),
                      );
                    }}
                    className={
                      page >= Math.ceil(totalProducts / pageSize)
                        ? "pointer-events-none opacity-50"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </div>
    </div>
  );
}
